import { Component, type ErrorInfo, type ReactNode } from 'react';
import { KABUK_METIN } from './metin/kabuk';
import { YOLLAR } from './yonlendirme';

/**
 * Hata sınırı — iki katman.
 *
 * `web/src`te hiç `componentDidCatch` yoktu (ölçüldü, denetim ajanının bulgusu). Bir bileşenin
 * çizim sırasındaki hatası (`Workflow.tsx`, tanımsız bir alan okuması) React ağacını kökten
 * söküyordu: giriş, üst menü, panel, hepsi tek `TypeError` ile beyaz ekrana düşüyordu. Tek yol
 * sayfayı elle yenilemekti ve kullanıcı neden olduğunu hiç görmüyordu.
 *
 * İki katmanın gerçeği farklı, o yüzden metinleri de farklı (`metin/kabuk.ts` `hataSiniri`):
 *
 *   - **Uygulama katmanı** (`App.tsx`, `seviye="uygulama"`): bu çöktüğünde altındaki her şey
 *     gitti, geriye "sayfayı yenile"den başka gerçek bir çıkış yok.
 *   - **Bölüm katmanı** (`App.tsx` `Workspace` içinde, her sayfa geçişinde `key={yol}` ile
 *     yeniden kuruluyor): üst şerit ve menü bu sınırın **dışında**, yani bir bölüm çökse bile
 *     ayakta kalıyorlar — "ana sayfaya dön" burada gerçek bir seçenek.
 *
 * React'ın hata sınırı yalnız sınıf bileşeninde var (`getDerivedStateFromError`/
 * `componentDidCatch`in kanca karşılığı yok), o yüzden bu dosya tek yerde `class` kullanıyor.
 */

const GUNLUK_ANAHTARI = 'mbd-crm-hata-gunlugu';
/** Günlük büyümesin diye tutulan en fazla kayıt. */
const GUNLUK_SINIRI = 20;

interface GunlukKaydi {
  zaman: string;
  baglam: string;
  mesaj: string;
  yigin: string | null;
  bilesenYigini: string | null;
}

/**
 * Teknik ayrıntı bugüne kadar yalnız tarayıcı konsolundaydı ve kimse bakmıyordu. `console.error`
 * duruyor (geliştirici araçları hâlâ en hızlı yol), ama yanına `localStorage`a yazılan bir günlük
 * eklendi: sayfa yenilendikten, hatta sekme kapatılıp açıldıktan sonra da okunabiliyor. Sunucuya
 * göndermek daha doğrusu olurdu ama bunun ucu yok (`api/src`te bir hata bildirme ucu yok, bu API
 * tarafına bildirildi) — bu yüzden şimdilik yerel günlük.
 */
function gunlugeYaz(hata: Error, bilgi: ErrorInfo, baglam: string): void {
  console.error(`[hata sınırı: ${baglam}]`, hata, bilgi.componentStack);
  try {
    const kayit: GunlukKaydi = {
      zaman: new Date().toISOString(),
      baglam,
      mesaj: hata.message,
      yigin: hata.stack ?? null,
      bilesenYigini: bilgi.componentStack ?? null,
    };
    const mevcut: unknown = JSON.parse(window.localStorage.getItem(GUNLUK_ANAHTARI) ?? '[]');
    const liste = Array.isArray(mevcut) ? (mevcut as GunlukKaydi[]) : [];
    window.localStorage.setItem(GUNLUK_ANAHTARI, JSON.stringify([...liste, kayit].slice(-GUNLUK_SINIRI)));
  } catch {
    // Depolama dolu ya da kapalıysa günlük atlanıyor; konsoldaki kayıt zaten yazıldı.
  }
}

interface HataSiniriProps {
  children: ReactNode;
  seviye: 'uygulama' | 'bolum';
  /** Yalnız `seviye="bolum"` iken: çöken bölümün insanın okuduğu adı. */
  bolumAdi?: string;
}

interface HataSiniriState {
  hata: Error | null;
}

export class HataSiniri extends Component<HataSiniriProps, HataSiniriState> {
  state: HataSiniriState = { hata: null };

  static getDerivedStateFromError(hata: Error): HataSiniriState {
    return { hata };
  }

  componentDidCatch(hata: Error, bilgi: ErrorInfo): void {
    gunlugeYaz(hata, bilgi, this.props.seviye === 'bolum' ? (this.props.bolumAdi ?? 'bölüm') : 'uygulama');
  }

  private yenidenDene = (): void => this.setState({ hata: null });

  override render(): ReactNode {
    const { hata } = this.state;
    if (!hata) return this.props.children;

    const metin = KABUK_METIN.hataSiniri;
    const bolumSeviyesi = this.props.seviye === 'bolum';
    const testId = bolumSeviyesi ? 'section-error-boundary' : 'app-error-boundary';

    return (
      <div className="error-boundary callout" role="alert" data-testid={testId}>
        <div>
          <strong>{bolumSeviyesi ? metin.bolumBaslik(this.props.bolumAdi ?? '') : metin.uygulamaBaslik}</strong>
          <p>{bolumSeviyesi ? metin.bolumGovde : metin.uygulamaGovde}</p>
        </div>
        <div className="error-boundary-actions">
          <button type="button" data-testid="error-boundary-retry" onClick={this.yenidenDene}>
            {metin.yenidenDene}
          </button>
          {bolumSeviyesi ? (
            <a href={YOLLAR.genelBakis} data-testid="error-boundary-home">
              {metin.anaSayfayaDon}
            </a>
          ) : (
            <button type="button" onClick={() => window.location.reload()}>
              {metin.sayfayiYenile}
            </button>
          )}
        </div>
        <details>
          <summary>{metin.teknikAyrinti}</summary>
          <code>{hata.message}</code>
          <p className="muted">{metin.gunlukNotu}</p>
        </details>
      </div>
    );
  }
}

/**
 * Kasıtlı test tetikleyicisi.
 *
 * `?hataSinirTesti=uygulama` ya da `?hataSinirTesti=bolum` adres parametresiyle çizim sırasında
 * kasıtlı bir hata fırlatıyor — `tests/e2e/hata-siniri.spec.ts` bunu kullanıyor. Parametre
 * yoksa hiçbir şey çizmiyor; gerçek bir kullanıcı bu adrese rastlayarak bir şey kırmaz, en
 * kötü ihtimalle hata sınırının kendi kurtarma ekranını görür.
 */
export function HataTetikleyici({ seviye }: { seviye: 'uygulama' | 'bolum' }): null {
  const parametre = new URLSearchParams(window.location.search).get('hataSinirTesti');
  if (parametre === seviye) {
    throw new Error(`Kasıtlı test hatası (${seviye}): hata sınırının yakaladığını doğrulamak için.`);
  }
  return null;
}
